/**
 * useConditionalService hook - 支持条件请求的服务Hook
 * @author wzwwu
 */
import { useState, useEffect, useRef } from 'react'
import { CommonError } from '@types'
import { cache, isFunction } from '@library'
const _prefix = 'USECONDITIONALSERVICE-'

/**
 * 将普通的 Service Function 转换成支持条件请求的 hook
 * @param {service} service 请求方法
 * @param {boolean} condition 是否执行请求的条件
 * @param {...any[]} data 该 service 方法所需要的参数
 */
export function useConditionalService<T, D extends any[]> (
  service: (...data: D) => Promise<[T, CommonError]>,
  condition: boolean,
  ...data: D
) {
  const key = _prefix + `${service.name}${JSON.stringify(data)}`
  const [flag, setFlag] = useState(0)
  const [loading, setLoading] = useState(false)
  const [res, setRes] = useState<[T, CommonError]>([null, null] as unknown as [T, CommonError])
  // 添加请求标识符引用，用于跟踪最新的请求
  const requestIdRef = useRef(0)
  // 记录是否已经执行过请求
  const hasRequestedRef = useRef(false)

  useEffect(() => {
    if (!isFunction(service) || !condition) return

    // 只有满足条件时才设置loading状态
    setLoading(true)

    // 增加请求标识符
    const currentRequestId = ++requestIdRef.current

    ;(async () => {
      // 请求
      const res = await service(...data)

      // 只有当前请求是最新的请求时，才更新状态
      if (currentRequestId === requestIdRef.current) {
        setRes(res)
        cache.set(key, res)
        setLoading(false)
        hasRequestedRef.current = true
      }
    })()

    return () => {
      // 清理函数，可选
    }
  }, [...data, flag, condition]) // 监听 data、condition 变化，重新执行 effect

  // 手动触发请求的方法，只有在满足条件时才会实际发送请求
  const forceRequest = () => {
    if (condition) {
      setFlag(f => f + 1)
    }
  }

  // 检查是否已经请求过
  const hasRequested = hasRequestedRef.current

  return [loading, res[0], res[1], forceRequest, hasRequested] as const
}
